"use client";

import { useState, useEffect } from "react";

const UserManagementForm = ({ user, onSave, onCancel, roles }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: roles?.[0]?._id || "", // default to the first role if available
    password: "",
  });

  // When editing a user, prefill fields (including setting role to the role's _id)
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        role: user.role?._id || user.role || roles?.[0]?._id || "",
        password: "", // Do not prefill password in edit mode
      });
    }
  }, [user, roles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const dataToSave = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      role: formData.role, // now directly the ObjectId from dynamic roles
    };

    // Include password only in add mode and if provided
    if (!user && formData.password) {
      dataToSave.password = formData.password;
    }

    if (user) {
      const userId = user._id;
      if (!userId) {
        import("sweetalert2").then((Swal) => {
          Swal.default.fire({
            icon: "error",
            title: "Error",
            text: "Invalid user ID",
          });
        });
        return;
      }
      onSave(userId, dataToSave);
    } else {
      onSave(dataToSave);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="p-6 space-y-6 bg-white rounded-lg shadow-lg w-96"
    >
      <div className="form-group">
        <label
          htmlFor="firstName"
          className="block text-sm font-medium text-gray-700"
        >
          First Name
        </label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          required
          className="block w-full px-4 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="form-group">
        <label
          htmlFor="lastName"
          className="block text-sm font-medium text-gray-700"
        >
          Last Name
        </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          required
          className="block w-full px-4 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="form-group">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="block w-full px-4 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {!user && (
        <div className="form-group">
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700"
          >
            Password
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            className="block w-full px-4 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      <div className="form-group">
        <label
          htmlFor="role"
          className="block text-sm font-medium text-gray-700"
        >
          Role
        </label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          required
          className="block w-full px-4 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {roles && roles.length > 0 ? (
            roles.map((role) => (
              <option key={role._id} value={role._id}>
                {role.name}
              </option>
            ))
          ) : (
            <option value="">Loading roles...</option>
          )}
        </select>
      </div>

      <div className="flex items-center justify-between form-actions">
        <button
          type="button"
          className="px-6 py-2 text-white transition-all bg-gray-500 rounded-md hover:bg-gray-600"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-2 text-white transition-all bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {user ? "Update User" : "Add User"}
        </button>
      </div>
    </form>
  );
};

export default UserManagementForm;
